<template>
  <div class="flex w-full h-screen overflow-hidden bg-slate-100">
    <div class="flex-1 w-full overflow-hidden">
      <Topbar />

      <div class="mt-[64px] flex h-[calc(100vh-64px)] flex-col overflow-hidden p-6">
        <div class="mb-4 flex shrink-0 items-start justify-between gap-3">
          <div>
            <h2 class="flex items-center gap-2 text-2xl font-semibold tracking-tight text-slate-900 md:text-3xl">
              Список товаров на складе
            </h2>
            <p class="mt-1 text-sm text-slate-500 md:text-base">Актуальные остатки и карточки товаров склада</p>
          </div>
          <Button label="Обновить" icon="pi pi-refresh" severity="secondary" outlined class="!rounded-xl !border-slate-200 !shadow-sm" @click="fetchProducts" />
        </div>

        <Toast />

        <div class="min-h-0 flex-1 overflow-hidden rounded-2xl">
          <Card class="flex h-full min-h-0 flex-col overflow-hidden">
          <template #content>
            <div class="flex h-full min-h-0 flex-col">
              <div class="mb-4 flex shrink-0 items-center justify-between gap-3">
                <div class="flex w-full max-w-[48rem] items-center gap-3">
                  <div class="flex-1">
                    <IconField class="w-full">
                      <InputIcon><i class="pi pi-search" /></InputIcon>
                      <InputText v-model.trim="query" placeholder="Поиск по названию, штрихкоду, классу, категории..." class="w-full" />
                    </IconField>
                  </div>

                  <Select
                    v-model="selectedClassification"
                    :options="classificationOptions"
                    optionLabel="label"
                    optionValue="value"
                    placeholder="Все классы"
                    class="w-56 shrink-0"
                    showClear
                  />
                  <Select
                    v-model="selectedCategory"
                    :options="categoryOptions"
                    optionLabel="label"
                    optionValue="value"
                    placeholder="Все категории"
                    class="w-56 shrink-0"
                    :disabled="!selectedClassification"
                    showClear
                  />
                </div>

                <div class="flex items-center gap-2">
                  <Button
                    :label="`Низкий остаток (${lowStockCount})`"
                    icon="pi pi-exclamation-circle"
                    size="small"
                    :severity="activeQuickFilter === 'low-stock' ? 'warn' : 'secondary'"
                    :outlined="activeQuickFilter !== 'low-stock'"
                    :class="activeQuickFilter === 'low-stock' ? '!shadow-sm' : '!border-amber-200 !bg-amber-50 !text-amber-800 hover:!bg-amber-100'"
                    @click="toggleQuickFilter('low-stock')"
                  />
                  <Button
                    :label="`Истекает срок (${expiringSoonCount})`"
                    icon="pi pi-clock"
                    size="small"
                    :severity="activeQuickFilter === 'expiring' ? 'warn' : 'secondary'"
                    :outlined="activeQuickFilter !== 'expiring'"
                    :class="activeQuickFilter === 'expiring' ? '!shadow-sm' : '!border-orange-200 !bg-orange-50 !text-orange-800 hover:!bg-orange-100'"
                    @click="toggleQuickFilter('expiring')"
                  />
                  <Button
                    label="Сетка"
                    icon="pi pi-th-large"
                    size="small"
                    :severity="viewMode === 'grid' ? 'success' : 'secondary'"
                    :outlined="viewMode !== 'grid'"
                    @click="viewMode = 'grid'"
                  />
                  <Button
                    label="Таблица"
                    icon="pi pi-table"
                    size="small"
                    :severity="viewMode === 'table' ? 'success' : 'secondary'"
                    :outlined="viewMode !== 'table'"
                    @click="viewMode = 'table'"
                  />
                </div>
              </div>

              <div v-if="loading" class="min-h-0 flex-1 overflow-y-auto rounded-2xl border border-slate-200 bg-slate-50 p-4 pr-1">
                <div class="py-12 text-center text-slate-500">Загрузка товаров...</div>
              </div>

              <div v-else-if="!filteredProducts.length" class="min-h-0 flex-1 overflow-y-auto rounded-2xl border border-slate-200 bg-slate-50 p-4 pr-1">
                <div class="py-12 text-center text-slate-500">Товары не найдены</div>
              </div>

              <div v-else-if="viewMode === 'grid'" class="min-h-0 flex-1 overflow-y-auto rounded-2xl border border-slate-200 bg-slate-50 p-4 pr-1">
                <div class="grid grid-cols-1 gap-4 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6">
                  <article
                    v-for="item in filteredProducts"
                    :key="item.id"
                    class="relative cursor-pointer overflow-hidden rounded-2xl border shadow-sm transition-colors"
                    :class="getProductCardClass(item)"
                  >
                    <Button
                      v-if="primaryBarcode(item.barcode)"
                      icon="pi pi-file-pdf"
                      severity="secondary"
                      outlined
                      rounded
                      size="small"
                      class="!absolute right-3 top-3 z-10 !h-8 !w-8 !border-white/80 !bg-white/92 !text-slate-700 shadow-sm backdrop-blur-sm"
                      @click.stop="exportProductBarcodePdf(item)"
                    />

                    <div class="flex h-40 items-center justify-center overflow-hidden bg-slate-100">
                      <Image
                        v-if="item.image"
                        :src="item.image"
                        :alt="item.name"
                        class="flex h-full w-full items-center justify-center"
                        imageClass="w-full h-full object-contain cursor-zoom-in"
                        preview
                      />
                      <div v-else class="flex h-full w-full items-center justify-center text-sm text-slate-400">Нет фото</div>
                    </div>

                    <div class="space-y-2 p-3">
                      <h3 class="min-h-[2.5rem] text-sm font-semibold line-clamp-2">{{ item.name }}</h3>
                      <div class="flex min-h-[1.5rem] flex-wrap gap-1">
                        <span
                          v-for="code in displayBarcodes(item.barcode)"
                          :key="`${item.id}-${code}`"
                          class="rounded bg-gray-100 px-2 py-0.5 text-[10px] text-gray-700"
                        >
                          {{ code }}
                        </span>
                        <span
                          v-if="hiddenBarcodesCount(item.barcode) > 0"
                          class="rounded bg-green-100 px-2 py-0.5 text-[10px] font-medium text-green-700"
                        >
                          +{{ hiddenBarcodesCount(item.barcode) }}
                        </span>
                      </div>
                      <p class="text-xs text-slate-500">
                        {{ item.classification || '—' }} -> {{ item.category || '—' }}
                      </p>
                      <p
                        class="min-h-[2rem] text-xs"
                        :class="isExpiringWithinThreeMonths(item.date) ? 'font-semibold text-amber-700' : 'text-slate-600'"
                      >
                        Срок годности: {{ item.date || '—' }}
                      </p>

                      <div class="flex items-center justify-between pt-2 text-xs">
                        <span
                          class="rounded px-2 py-1"
                          :class="isLowStock(item) ? 'bg-amber-100 font-medium text-amber-800' : 'bg-emerald-50 text-emerald-700'"
                        >
                          Остаток: {{ item.amount }} {{ getUnitLabel(item) }}
                        </span>
                        <span class="font-semibold text-slate-700">{{ item.price }} ₸</span>
                      </div>
                    </div>
                  </article>
                </div>
              </div>

              <div v-else class="min-h-0 flex-1 overflow-auto rounded-2xl border border-slate-200 bg-white shadow-sm">
                  <div class="min-w-max">
                    <table class="min-w-full divide-y divide-slate-200 text-sm">
                      <thead class="bg-slate-50">
                        <tr>
                          <th class="sticky top-0 z-10 bg-slate-50 px-4 py-3 text-left font-semibold text-slate-600">Товар</th>
                          <th class="sticky top-0 z-10 bg-slate-50 px-4 py-3 text-left font-semibold text-slate-600">Штрихкоды</th>
                          <th class="sticky top-0 z-10 bg-slate-50 px-4 py-3 text-left font-semibold text-slate-600">Класс</th>
                          <th class="sticky top-0 z-10 bg-slate-50 px-4 py-3 text-left font-semibold text-slate-600">Категория</th>
                          <th class="sticky top-0 z-10 bg-slate-50 px-4 py-3 text-left font-semibold text-slate-600">Срок годности</th>
                          <th class="sticky top-0 z-10 bg-slate-50 px-4 py-3 text-left font-semibold text-slate-600">Остаток</th>
                          <th class="sticky top-0 z-10 bg-slate-50 px-4 py-3 text-left font-semibold text-slate-600">Цена</th>
                        </tr>
                      </thead>
                      <tbody class="divide-y divide-slate-100 bg-white">
                        <tr v-for="item in filteredProducts" :key="item.id" class="align-top transition-colors" :class="getProductRowClass(item)">
                          <td class="px-4 py-3">
                            <div class="flex items-center gap-3">
                              <div class="flex h-10 w-10 shrink-0 items-center justify-center overflow-hidden rounded-xl bg-slate-100">
                                <img v-if="item.image" :src="item.image" :alt="item.name" class="h-full w-full object-contain" />
                                <i v-else class="pi pi-image text-slate-300"></i>
                              </div>
                              <div class="min-w-0">
                                <p class="truncate font-semibold text-slate-900">{{ item.name }}</p>
                                <p
                                  class="text-xs"
                                  :class="isExpiringWithinThreeMonths(item.date) ? 'font-semibold text-amber-700' : 'text-slate-500'"
                                >
                                  Срок годности: {{ item.date || '—' }}
                                </p>
                              </div>
                            </div>
                          </td>
                          <td class="px-4 py-3 text-slate-600">
                            <div class="flex max-w-xs flex-wrap gap-1">
                              <span
                                v-for="code in displayBarcodes(item.barcode, 6)"
                                :key="`${item.id}-table-${code}`"
                                class="rounded bg-slate-100 px-2 py-0.5 text-[10px] text-slate-700"
                              >
                                {{ code }}
                              </span>
                              <span
                                v-if="hiddenBarcodesCount(item.barcode, 6) > 0"
                                class="rounded bg-emerald-100 px-2 py-0.5 text-[10px] font-medium text-emerald-700"
                              >
                                +{{ hiddenBarcodesCount(item.barcode, 6) }}
                              </span>
                            </div>
                          </td>
                          <td class="px-4 py-3 text-slate-600">{{ item.classification || '—' }}</td>
                          <td class="px-4 py-3 text-slate-600">{{ item.category || '—' }}</td>
                          <td
                            class="px-4 py-3"
                            :class="isExpiringWithinThreeMonths(item.date) ? 'font-semibold text-amber-700' : 'text-slate-600'"
                          >
                            {{ item.date || '—' }}
                          </td>
                          <td class="px-4 py-3">
                            <span
                              class="rounded px-2 py-1 text-xs"
                              :class="isLowStock(item) ? 'bg-amber-100 font-medium text-amber-800' : 'bg-emerald-50 text-emerald-700'"
                            >
                              {{ item.amount }} {{ getUnitLabel(item) }}
                            </span>
                          </td>
                          <td class="px-4 py-3 font-semibold text-slate-900">{{ item.price }} ₸</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
            </div>
          </template>
          </Card>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, onMounted, ref, watch } from 'vue'
import axios from '../lib/axios'
import { useToast } from 'primevue/usetoast'
import Toast from 'primevue/toast'
import { exportBarcodePdfLabel } from '../utils/barcodePdf'

const toast = useToast()

const loading = ref(false)
const query = ref('')
const viewMode = ref('grid')
const activeQuickFilter = ref('all')
const selectedClassification = ref(null)
const selectedCategory = ref(null)
const products = ref([])

const inStockProducts = computed(() => {
  return products.value.filter((item) => Number(item.amount || 0) > 0)
})

const classificationOptions = computed(() => {
  const uniqueClassifications = [...new Set(
    inStockProducts.value
      .map((item) => String(item.classification || '').trim())
      .filter(Boolean)
  )].sort((a, b) => a.localeCompare(b, 'ru'))

  return uniqueClassifications.map((item) => ({
    label: item,
    value: item
  }))
})

const categoryOptions = computed(() => {
  const scopedProducts = selectedClassification.value
    ? inStockProducts.value.filter((item) => String(item.classification || '').trim() === selectedClassification.value)
    : inStockProducts.value

  const uniqueCategories = [...new Set(
    scopedProducts
      .map((item) => String(item.category || '').trim())
      .filter(Boolean)
  )].sort((a, b) => a.localeCompare(b, 'ru'))

  return uniqueCategories.map((item) => ({
    label: item,
    value: item
  }))
})

const baseFilteredProducts = computed(() => {
  const q = query.value.trim().toLowerCase()
  const classFilteredProducts = selectedClassification.value
    ? inStockProducts.value.filter((item) => String(item.classification || '').trim() === selectedClassification.value)
    : inStockProducts.value

  const categoryFilteredProducts = selectedCategory.value
    ? classFilteredProducts.filter((item) => String(item.category || '').trim() === selectedCategory.value)
    : classFilteredProducts

  if (!q) return categoryFilteredProducts

  return categoryFilteredProducts.filter((item) => {
    const name = (item.name || '').toLowerCase()
    const barcodes = normalizeBarcodes(item.barcode).join(' ').toLowerCase()
    const classification = (item.classification || '').toLowerCase()
    const category = (item.category || '').toLowerCase()

    return name.includes(q) || barcodes.includes(q) || classification.includes(q) || category.includes(q)
  })
})

const lowStockCount = computed(() => {
  return baseFilteredProducts.value.filter((item) => isLowStock(item)).length
})

const expiringSoonCount = computed(() => {
  return baseFilteredProducts.value.filter((item) => isExpiringWithinThreeMonths(item.date)).length
})

const filteredProducts = computed(() => {
  return baseFilteredProducts.value.filter((item) => {
    if (activeQuickFilter.value === 'low-stock') {
      return isLowStock(item)
    }

    if (activeQuickFilter.value === 'expiring') {
      return isExpiringWithinThreeMonths(item.date)
    }

    return true
  })
})

watch(selectedClassification, (nextValue, previousValue) => {
  if (nextValue !== previousValue) {
    selectedCategory.value = null
  }
})

const isLowStock = (item) => {
  return Number(item?.amount || 0) < 5
}

const getUnitLabel = (item) => {
  if (typeof item?.unit === 'string' && item.unit.trim()) return item.unit.trim()
  if (typeof item?.unit_name === 'string' && item.unit_name.trim()) return item.unit_name.trim()
  if (typeof item?.unit?.name === 'string' && item.unit.name.trim()) return item.unit.name.trim()
  if (typeof item?.unit?.label === 'string' && item.unit.label.trim()) return item.unit.label.trim()
  return ''
}

const hasProductWarning = (item) => {
  return isLowStock(item) || isExpiringWithinThreeMonths(item?.date)
}

const getProductCardClass = (item) => {
  const isLow = isLowStock(item)
  const isExpiring = isExpiringWithinThreeMonths(item?.date)

  if (isLow && isExpiring) {
    return 'border-orange-300 bg-gradient-to-br from-amber-100 to-orange-100 hover:border-orange-400'
  }

  if (isLow) {
    return 'border-amber-300 bg-amber-100/80 hover:border-amber-400'
  }

  if (isExpiring) {
    return 'border-orange-300 bg-orange-100/80 hover:border-orange-400'
  }

  return 'border-slate-200 bg-white hover:border-emerald-200'
}

const getProductRowClass = (item) => {
  const isLow = isLowStock(item)
  const isExpiring = isExpiringWithinThreeMonths(item?.date)

  if (isLow && isExpiring) {
    return 'bg-gradient-to-r from-amber-100 to-orange-100 hover:from-amber-200 hover:to-orange-200'
  }

  if (isLow) {
    return 'bg-amber-100/80 hover:bg-amber-200/70'
  }

  if (isExpiring) {
    return 'bg-orange-100/80 hover:bg-orange-200/70'
  }

  return 'bg-white hover:bg-slate-50'
}

const toggleQuickFilter = (filterName) => {
  activeQuickFilter.value = activeQuickFilter.value === filterName ? 'all' : filterName
}

const EXPIRY_WARNING_MONTHS = 3

const isExpiringWithinThreeMonths = (value) => {
  if (!value) return false

  const expiryDate = new Date(value)
  if (Number.isNaN(expiryDate.getTime())) return false

  expiryDate.setHours(0, 0, 0, 0)

  const today = new Date()
  today.setHours(0, 0, 0, 0)

  const warningDate = new Date(today)
  warningDate.setMonth(warningDate.getMonth() + EXPIRY_WARNING_MONTHS)
  warningDate.setHours(0, 0, 0, 0)

  return expiryDate >= today && expiryDate <= warningDate
}

const extractBarcodeValue = (value) => {
  if (value === null || value === undefined || value === '') return null

  if (typeof value === 'number') return String(value)

  if (typeof value === 'string') {
    try {
      const parsed = JSON.parse(value)
      if (parsed && typeof parsed === 'object' && parsed.barcode !== undefined) {
        return String(parsed.barcode)
      }
      return value
    } catch {
      return value
    }
  }

  if (typeof value === 'object') {
    if (value.barcode !== undefined && value.barcode !== null) {
      return String(value.barcode)
    }
    return null
  }

  return null
}

const normalizeBarcodes = (rawBarcode) => {
  const source = Array.isArray(rawBarcode)
    ? rawBarcode
    : rawBarcode && typeof rawBarcode === 'object' && Array.isArray(rawBarcode.data)
      ? rawBarcode.data
      : [rawBarcode]

  const parsed = source
    .map((item) => extractBarcodeValue(item))
    .filter((item) => item && item.trim() !== '')

  return [...new Set(parsed)]
}

const displayBarcodes = (rawBarcode, limit = 4) => {
  return normalizeBarcodes(rawBarcode).slice(0, limit)
}

const hiddenBarcodesCount = (rawBarcode, limit = 4) => {
  const total = normalizeBarcodes(rawBarcode).length
  return total > limit ? total - limit : 0
}

const primaryBarcode = (rawBarcode) => {
  return normalizeBarcodes(rawBarcode)[0] || ''
}

const EAN13_LEFT_ODD = {
  '0': '0001101',
  '1': '0011001',
  '2': '0010011',
  '3': '0111101',
  '4': '0100011',
  '5': '0110001',
  '6': '0101111',
  '7': '0111011',
  '8': '0110111',
  '9': '0001011'
}

const EAN13_LEFT_EVEN = {
  '0': '0100111',
  '1': '0110011',
  '2': '0011011',
  '3': '0100001',
  '4': '0011101',
  '5': '0111001',
  '6': '0000101',
  '7': '0010001',
  '8': '0001001',
  '9': '0010111'
}

const EAN13_RIGHT = {
  '0': '1110010',
  '1': '1100110',
  '2': '1101100',
  '3': '1000010',
  '4': '1011100',
  '5': '1001110',
  '6': '1010000',
  '7': '1000100',
  '8': '1001000',
  '9': '1110100'
}

const EAN13_PARITY = {
  '0': 'OOOOOO',
  '1': 'OOEOEE',
  '2': 'OOEEOE',
  '3': 'OOEEEO',
  '4': 'OEOOEE',
  '5': 'OEEOOE',
  '6': 'OEEEOO',
  '7': 'OEOEOE',
  '8': 'OEOEEO',
  '9': 'OEEOEO'
}

const escapePdfText = (value) => String(value || '').replace(/\\/g, '\\\\').replace(/\(/g, '\\(').replace(/\)/g, '\\)')
const toPdfAscii = (value) => String(value || '').replace(/[^\x20-\x7E]/g, '').trim()

const calculateEan13CheckDigit = (digits12) => {
  const numbers = digits12.split('').map(Number)
  const total = numbers.reduce((sum, digit, index) => sum + digit * (index % 2 === 0 ? 1 : 3), 0)
  return String((10 - (total % 10)) % 10)
}

const resolveEan13Barcode = (barcode) => {
  const digits = String(barcode || '').replace(/\D/g, '')
  if (digits.length === 12) {
    return digits + calculateEan13CheckDigit(digits)
  }
  return digits.length === 13 ? digits : ''
}

const buildEan13Pattern = (barcode) => {
  const digits = resolveEan13Barcode(barcode)
  if (!digits) return null

  const first = digits[0]
  const leftDigits = digits.slice(1, 7).split('')
  const rightDigits = digits.slice(7).split('')
  const parity = EAN13_PARITY[first]

  if (!parity) return null

  const leftPattern = leftDigits.map((digit, index) => {
    return parity[index] === 'E' ? EAN13_LEFT_EVEN[digit] : EAN13_LEFT_ODD[digit]
  }).join('')

  const rightPattern = rightDigits.map((digit) => EAN13_RIGHT[digit]).join('')

  return {
    digits,
    pattern: `101${leftPattern}01010${rightPattern}101`
  }
}

const downloadPdf = (fileName, content) => {
  const blob = new Blob([content], { type: 'application/pdf' })
  const url = URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = url
  link.download = fileName
  link.click()
  URL.revokeObjectURL(url)
}

const createBarcodePdf = ({ titleText, barcode }) => {
  const width = 595.28
  const height = 841.89
  const bars = buildEan13Pattern(barcode)
  const normalizedBarcode = bars?.digits || String(barcode || '').trim()
  const title = String(titleText || '').trim() || 'Товар'
  const lines = [
    'BT',
    '/F1 11 Tf',
    `50 ${height - 44} Td`,
    `(${escapePdfText('Штрих-код товара')}) Tj`,
    'ET',
    'BT',
    '/F1 8 Tf',
    `50 ${height - 60} Td`,
    `(${escapePdfText(title)}) Tj`,
    'ET',
  ]

  if (bars) {
    const patternLength = bars.pattern.length
    const barcodeWidth = 132
    const barWidth = barcodeWidth / patternLength
    const startX = 116
    const baseY = 610
    const barHeight = 68
    const guardHeight = 74
    const digitY = baseY - 16
    const firstDigitX = startX - 18
    const leftDigitsX = startX + (3 * barWidth) + 3
    const rightDigitsX = startX + (50 * barWidth) + 3
    const arrowStartX = startX + barcodeWidth + 5
    const arrowCenterY = digitY + 1.5
    const guardIndexes = new Set([0, 2, 45, 47, 92, 94])

    lines.push('0 g')
    bars.pattern.split('').forEach((bit, index) => {
      if (bit !== '1') return
      const currentX = startX + index * barWidth
      const currentHeight = guardIndexes.has(index) ? guardHeight : barHeight
      lines.push(`${currentX.toFixed(2)} ${baseY.toFixed(2)} ${barWidth.toFixed(2)} ${currentHeight.toFixed(2)} re f`)
    })

    lines.push('BT')
    lines.push('/F1 14 Tf')
    lines.push(`${firstDigitX.toFixed(2)} ${digitY.toFixed(2)} Td`)
    lines.push(`(${bars.digits[0]}) Tj`)
    lines.push('ET')

    lines.push('BT')
    lines.push('/F1 12 Tf')
    lines.push(`${leftDigitsX.toFixed(2)} ${digitY.toFixed(2)} Td`)
    lines.push(`(${bars.digits.slice(1, 7)}) Tj`)
    lines.push('ET')

    lines.push('BT')
    lines.push('/F1 12 Tf')
    lines.push(`${rightDigitsX.toFixed(2)} ${digitY.toFixed(2)} Td`)
    lines.push(`(${bars.digits.slice(7)}) Tj`)
    lines.push('ET')

    lines.push(`${arrowStartX.toFixed(2)} ${(arrowCenterY + 3.5).toFixed(2)} m ${(arrowStartX + 7).toFixed(2)} ${arrowCenterY.toFixed(2)} l ${(arrowStartX).toFixed(2)} ${(arrowCenterY - 3.5).toFixed(2)} l S`)
  } else {
    lines.push('BT')
    lines.push('/F1 18 Tf')
    lines.push(`50 ${height - 160} Td`)
    lines.push(`(${escapePdfText('Неподдерживаемый формат штрих-кода')}) Tj`)
    lines.push('ET')

    lines.push('BT')
    lines.push('/F1 28 Tf')
    lines.push(`50 ${height - 220} Td`)
    lines.push(`(${escapePdfText(normalizedBarcode || 'Нет штрих-кода')}) Tj`)
    lines.push('ET')
  }

  lines.push('BT')
  lines.push('/F1 7 Tf')
  lines.push(`50 42 Td`)
  lines.push(`(${escapePdfText(`Код: ${normalizedBarcode || 'Не назначен'}`)}) Tj`)
  lines.push('ET')

  const stream = `${lines.join('\n')}\n`
  const objects = []

  objects.push('1 0 obj\n<< /Type /Catalog /Pages 2 0 R >>\nendobj')
  objects.push('2 0 obj\n<< /Type /Pages /Count 1 /Kids [3 0 R] >>\nendobj')
  objects.push(`3 0 obj
<< /Type /Page /Parent 2 0 R /MediaBox [0 0 ${width} ${height}] /Resources << /Font << /F1 4 0 R >> >> /Contents 5 0 R >>
endobj`)
  objects.push('4 0 obj\n<< /Type /Font /Subtype /Type1 /BaseFont /Helvetica >>\nendobj')
  objects.push(`5 0 obj
<< /Length ${stream.length} >>
stream
${stream}endstream
endobj`)

  let pdf = '%PDF-1.4\n'
  const offsets = [0]

  objects.forEach((object) => {
    offsets.push(pdf.length)
    pdf += `${object}\n`
  })

  const xrefStart = pdf.length
  pdf += `xref
0 ${objects.length + 1}
0000000000 65535 f 
`

  offsets.slice(1).forEach((offset) => {
    pdf += `${String(offset).padStart(10, '0')} 00000 n \n`
  })

  pdf += `trailer
<< /Size ${objects.length + 1} /Root 1 0 R >>
startxref
${xrefStart}
%%EOF`

  return pdf
}

const exportProductBarcodePdf = async (item) => {
  const barcode = primaryBarcode(item?.barcode)
  if (!barcode) return

  try {
    await exportBarcodePdfLabel({
      heading: 'Штрих-код товара',
      title: item?.name || 'Товар',
      barcode,
      fileName: `Штрихкод товара-${item?.name || 'export'}.pdf`
    })
  } catch (error) {
    toast.add({
      severity: 'error',
      summary: 'Ошибка',
      detail: 'Не удалось экспортировать PDF штрих-кода',
      life: 3000
    })
  }
}

const fetchProducts = async () => {
  loading.value = true
  try {
    const res = await axios.get('/api/store', {
      params: {
        storage: 1,
        price_source: 'daily_only',
      },
    })
    products.value = Array.isArray(res.data) ? res.data : []
  } catch (error) {
    toast.add({
      severity: 'error',
      summary: 'Ошибка',
      detail: error?.response?.data?.message || 'Не удалось загрузить товары склада',
      life: 3000
    })
  } finally {
    loading.value = false
  }
}

onMounted(fetchProducts)
</script>

<style scoped>
:deep(.p-card),
:deep(.p-card-body),
:deep(.p-card-content) {
  height: 100%;
}

:deep(.p-card-body),
:deep(.p-card-content) {
  display: flex;
  flex: 1 1 auto;
  min-height: 0;
  flex-direction: column;
}
</style>
